import { FC, useEffect, useMemo } from "react"
import { useQueryClient } from "react-query"

import { Formik,FormikValues } from 'formik'

import * as Yup from 'yup'

import { KTSVG } from "../../../../../../../_metronic/helpers"
import {
  DLMROI,
  submitNewDLMApplication,
  submitDLMPhase_1_2,
  editDLMApplication
} from "../../../../../core/doclabel"

import { doFormSubmit, processNoteValues } from "../../../../../../components/helpers"
import { cloneDeep, pickBy, filter, sortBy } from "lodash"
import { DLMROIReplaceModalForm } from "./DLMROIReplaceModalForm"

const roiReplaceSchema: any = Yup.object({
  labelDetails: Yup.array().of(
    Yup.object().shape({
      targetDetailId: Yup.number().integer().label('ROI Detail Id'),
      labelCodes: Yup.array(),
      targetLabels: Yup.array().nullable()
    })
  ).required().min(1, 'Labels required'),
  otherAttachments: Yup.array(),
  policeReport: Yup.number().integer().moreThan(0, 'Police Report required').required(),
  policeReportAttachmentName: Yup.string().trim(),
  comment: Yup.string().trim().label('Comment').required(),
  hasTargets: Yup.number().integer().moreThan(0, 'Select labels to replace').required(),
  note: Yup.string().trim().label("Notes"),
  noteAttachments: Yup.array(),
  acknowledgeFee: Yup.bool().oneOf([true], 'Must be checked').label('Acknowledge Fee'),
});


const initROIReplaceFormValues = {
  labelDetails: [],
  otherAttachments: [],
  policeReport: 0,
  policeReportAttachmentName: '',
  comment: '',
  hasTargets: 0,
  note: '',
  noteAttachments:[],
  acknowledgeFee: false,
}

const ROI_COPY_LABEL_FIELDS = [
  'id',
  'targetDetailId',
  'targetLabels'
];

const ROI_RESTORE_FIELDS = [
  'comment',
  'otherAttachements',
  'policeReport',
  'policeReportAttachmentName'
]

type Props = {
  roiId: number
  roiNo: string
  baseROI: DLMROI
  doClose: Function
  onSuccess: Function
  appId?: number
  resubmitMode? : string
}

const DLMROIReplaceModal:FC<Props> = ({roiId, roiNo, baseROI, doClose, onSuccess, appId, resubmitMode = ''}) => {

  const queryClient = useQueryClient();

  // const [ initValues, setInitValues ] = useState<IDLMROIReplaceFormModel>(initROIReplaceFormValues);


  const initValues = useMemo(() => {

    let _init = cloneDeep(initROIReplaceFormValues);

    if (! baseROI) return _init;

    if (resubmitMode) {
      _init = {
        ...cloneDeep(initROIReplaceFormValues),
        ...pickBy(baseROI, (value, key) => (value && ROI_RESTORE_FIELDS.includes(key)))
      };
    }

    if (baseROI.hasOwnProperty('roiDetails') && baseROI.roiDetails.length > 0) {
      const _labelDetails: Array<any> = [];

      baseROI.roiDetails.forEach( (_detail: any) => {
        // @ts-ignore
        if (!  (_detail.hasOwnProperty('labels') && _detail.labels.length > 0)) return;

        // extract template
        let prefix = '';
        let places = 6;

        if (_detail.hasOwnProperty('labelFrom') && _detail.labelFrom) {
          const _matches = _detail.labelFrom.match(/^(\D*)(\d*)$/);
          if (_matches && _matches.length > 2) {
            prefix = _matches[1];
            places = _matches[2].length;
          }
        }
        // extract labels
        // @ts-ignore
        const labelCodes = filter(_detail.labels, (o) => { return (! o.deleted); }).map(
          label => (prefix + (`${label.serialNumber}`).padStart(places, '0'))
        ).sort();

        if (labelCodes.length > 0) {
          _labelDetails.push({
            id: (resubmitMode) ? _detail.id : 0,
            targetDetailId: _detail.id,
            labelFrom: _detail.labelFrom,
            labelTo: _detail.labelTo,
            labelCodes,
            targetLabels: _detail?.targetLabels ? _detail?.targetLabels : null,
            siteDetailId: _detail?.siteDetailId ? _detail?.siteDetailId : 0,
          })
        }
      })

      if (_labelDetails.length > 0) {
        Object.assign(_init, { labelDetails: sortBy(_labelDetails, ['labelFrom'], ['asc'])});
      }
    }
    return _init;
  }, [baseROI, resubmitMode])

  const submitFunc = useMemo(() => {
    switch(resubmitMode) {
    case 'resubmit':
        return submitDLMPhase_1_2;
    case 'edit':
        return editDLMApplication;
    default:
        return submitNewDLMApplication;
    }
  }, [resubmitMode]);



  const doSubmit = async (values: any, actions: FormikValues) => {

    const { setStatus, setSubmitting } = actions;

    const _labels = filter(values.labelDetails, (o) => { return (o.targetLabels && o.targetLabels.length > 0) }).map( item => {
      let _obj = pickBy(item, (value, key) => (value && ROI_COPY_LABEL_FIELDS.indexOf(key) > -1));
      if (resubmitMode && item.hasOwnProperty('siteDetailId')) {
        Object.assign(_obj, { id : item.siteDetailId });
      }
      return _obj;
    })


    const _site = {
      dlmWebPhase0DetailsModels: _labels
    };
    if ( resubmitMode && baseROI && baseROI.hasOwnProperty('siteId')) {
      // @ts-ignore
      Object.assign(_site, { id : baseROI.siteId});
    }

    let _payload = {
      ...pickBy(values, (value, key) => (value && (! (key.match(/^(note|hasTargets|labelDetails)/i)) ))),
      targetROI: roiId,
      applicationType: "TYPE_LABELREPLACEMENT",
      comment: values.comment,
      dlmWebPhase0SitesModel: [ _site ]
    };

    if (values.hasOwnProperty('otherAttachments') && values.otherAttachments.length > 0) {
      Object.assign(_payload, {otherAttachments: values.otherAttachments.map((o: any) => (o.id))});
    }

    if (appId) {
      Object.assign(_payload, { id: appId});
    }

    if (resubmitMode) {
      // @ts-ignore
      _payload = { application: _payload };
      processNoteValues(values, _payload);
    }
        
    await doFormSubmit(submitFunc, _payload, setStatus, setSubmitting, (res) => {
      queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { lastpath: 'label-applications-list' } ]);
      queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { listmode: 'own'} ]);
      if (appId) {
        queryClient.invalidateQueries(['DLMAPPLICATION-LOAD', { dlmappid: appId } ]);
      }
      if (res?.data) {
        onSuccess(res.data);
      }      
    });
  }

  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {  
      document.body.classList.remove('modal-open')
    }
  }, [])

  return (
  <>
  <div
    className='modal fade show d-block'
    id='kt_doclabel_roi_replace_modal'
    role='dialog'
    tabIndex={-1}
    aria-modal='true'
  >
    <div className='modal-dialog modal-dialog-centered mw-600px'>
      <div className='modal-content'>
        <div className='modal-header'>
          <h2 className='fw-bolder'>{`Label Replacement - ${roiNo}`}</h2>
          <div
            className='btn btn-icon btn-sm btn-active-icon-primary'
            data-kt-applicants-modal-action='close'
            onClick={()=> { doClose()}}
            style={{cursor: 'pointer'}}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
          </div>
        </div>
        <div className='modal-body px-8 py-6'>
          <Formik
            initialValues={initValues}
            validationSchema={roiReplaceSchema}
            enableReinitialize={true}
            onSubmit={doSubmit}
            component={(props: any) => <DLMROIReplaceModalForm doClose={doClose} resubmitMode={resubmitMode} {...props} />  }
          />
        </div>
      </div>
    </div>
  </div>
  <div className='modal-backdrop fade show'></div>
  </>
  )
}

export {DLMROIReplaceModal}